import { queryOptions } from '@tanstack/react-query';

import { queryClient } from '@/services/query-client';

import { fetchDirectInterlines } from '../data-provider';
import type { DirectInterline, DirectInterlineFilters, DirectInterlines } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';

export const directInterlinesQueryOptions = (
  filters: Partial<DirectInterlineFilters> = {},
  { enabled = true }: { enabled?: boolean } = {},
) =>
  queryOptions({
    enabled,
    queryFn: async ({ signal }) => {
      const directInterlines = await fetchDirectInterlines(filters, { signal });
      put(directInterlines.list);
      return directInterlines;
    },
    queryKey: CACHE_KEYS.directInterlines(filters),
    // biome-ignore lint/style/noMagicNumbers: 5 minutes
    staleTime: 1000 * 60 * 5,
  });

const INTERNAL_DIRECT_INTERLINES_CACHE_KEY = btoa('internal-direct-interlines-cache-key');

const INTERNAL_QUERY_DIRECT_INTERLINES_KEY = directInterlinesQueryOptions({
  codeDesc: INTERNAL_DIRECT_INTERLINES_CACHE_KEY,
}).queryKey;

const getDirectInterlineFallback = (directInterlineId: string): DirectInterline => {
  logger.warn(`Direct/InterLine "${directInterlineId}" was not found. Verify the data model.`);
  return {
    codeDesc: directInterlineId,
    id: directInterlineId,
    label: directInterlineId,
    lookupCodeType: 'DRCTIRLN',
  };
};

export const put = async (directInterlines: DirectInterline[]) => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_DIRECT_INTERLINES_KEY);

  const newDirectInterlines = directInterlines.reduce(
    (acc, directInterline) => {
      acc[String(directInterline.id)] = directInterline;
      return acc;
    },
    { ...(data?.map ?? {}) },
  );

  queryClient.setQueryData(INTERNAL_QUERY_DIRECT_INTERLINES_KEY, {
    list: Object.values(newDirectInterlines),
    map: newDirectInterlines,
  });
};

export const getAll = (): DirectInterlines | undefined => {
  return queryClient.getQueryData(INTERNAL_QUERY_DIRECT_INTERLINES_KEY);
};

export const find = async (directInterlineId: string): Promise<DirectInterline> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_DIRECT_INTERLINES_KEY);

  const directInterline = data?.map[directInterlineId];
  if (directInterline) {
    return directInterline;
  }

  try {
    const newData = await queryClient.query(directInterlinesQueryOptions({ search: { id: directInterlineId } }));
    if (newData.map[directInterlineId]) {
      void put(newData.list);
      return newData.map[directInterlineId];
    }
  } catch {
    return getDirectInterlineFallback(directInterlineId);
  }

  return getDirectInterlineFallback(directInterlineId);
};

export const findAll = async (directInterlineIds: string[]): Promise<DirectInterline[]> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_DIRECT_INTERLINES_KEY);

  const missingIds: string[] = [];
  const directInterlines: DirectInterline[] = [];

  for (const directInterlineId of directInterlineIds) {
    const directInterline = data?.map[directInterlineId];
    if (directInterline) {
      directInterlines.push(directInterline);
    } else {
      missingIds.push(directInterlineId);
    }
  }

  if (!missingIds.length) {
    return directInterlines;
  }

  try {
    const newData = await queryClient.query(directInterlinesQueryOptions({ search: { id: missingIds.join(',') } }));
    void put(newData.list);
    for (const missingId of missingIds) {
      const directInterline = newData.map[missingId];
      if (directInterline) {
        directInterlines.push(directInterline);
      } else {
        directInterlines.push(getDirectInterlineFallback(missingId));
      }
    }
  } catch {
    for (const missingId of missingIds) {
      directInterlines.push(getDirectInterlineFallback(missingId));
    }
  }

  return directInterlines;
};
